export const STATUS_RUNNING = "run";
export const STATUS_PAUSED = "pause";
export const STATUS_STOPPED = "stop";

class Timer {
  constructor(name) {
    if (name === undefined) {
      name = Math.random().toString(36).substring(2, 15);
    }
    this.name = name; // a label for the timer
    this.status = STATUS_STOPPED;
    this.elapsed = 0;
    this.reset();
  }

  // start a timer
  start() {
    // can't start a timer that is already running
    if (this.status === STATUS_RUNNING) {
      return;
    }

    this.started = new Date();
    this.status = STATUS_RUNNING;

    return this.started;
  }

  // pause a timer (used for automatic pausing during user idle time)
  pause() {
    // can't pause a timer unless it is running
    if (this.status !== STATUS_RUNNING) {
      return;
    }

    this.elapsed += (new Date() - this.started) / 1000;
    this.status = STATUS_PAUSED;

    return this.elapsed;
  }

  // convenience method to resume a timer that is paused or stopped
  resume() {
    return this.start();
  }

  // stop a timer (manual stop; can be resumed and does not clear elapsed time)
  stop() {
    // can't stop a timer that is not running
    if (this.status === STATUS_STOPPED) {
      return;
    }

    // update elapsed only if the timer is running (not paused)
    if (this.status === STATUS_RUNNING) {
      this.elapsed += (new Date() - this.started) / 1000;
    }

    this.status = STATUS_STOPPED;
    this.started = null;

    return this.elapsed;
  }

  // get the elapsed time, on-demand
  getElapsed(precision = 0) {
    let elapsed = this.elapsed;
    if (this.status === STATUS_RUNNING) {
      elapsed += ((new Date().getTime()) - this.started.getTime()) / 1000;
    }

    if (precision > 0) {
      const factor = Math.pow(10, precision);
      const tempNumber = elapsed * factor;
      const roundedTempNumber = Math.round(tempNumber);
      return roundedTempNumber / factor;
    } else {
      return Math.round(elapsed);
    }
  }

  // reset elapsed time and optionally stop the timer
  reset(stop = false) {
    this.elapsed = 0;

    if (stop === true) {
      this.status = STATUS_STOPPED;
      this.started = null;
    } else {
      this.started = new Date();
    }
  }

  // is the timer running
  isRunning() {
    return this.status === STATUS_RUNNING;
  }

  // is the timer paused
  isPaused() {
    return this.status === STATUS_PAUSED;
  }

  // is the timer stopped
  isStopped() {
    return this.status === STATUS_STOPPED;
  }

  /* istanbul ignore next */
  debug() {
    let statusMsg = "Unknown";
    if (this.status === STATUS_RUNNING) {
      statusMsg = "Running";
    } else if (this.status === STATUS_PAUSED) {
      statusMsg = "Paused";
    } else if (this.status === STATUS_STOPPED) {
      statusMsg = "Stopped";
    }

    const width = Math.max(this.name.length + 6, 20);
    const elapsedString = this.getElapsed() + "s";
    const headerWidth = Math.round((width - 7) / 2);
    const title = ("".padStart(headerWidth, "-") + " TIMER ").padEnd(
      width,
      "-"
    );

    const msg =
      " +" +
      title +
      "+\n" +
      (" | Name: " + this.name).padEnd(width) +
      "  |\n" +
      (" | Status: " + statusMsg).padEnd(width) +
      "  |\n" +
      (" | Elapsed: " + elapsedString).padEnd(width) +
      "  |\n" +
      " +" +
      "".padEnd(width, "-") +
      "+";

    if (console.debug) {
      console.debug(msg);
    } else {
      console.log(msg);
    }
  }
}

export default Timer;
